import { HttpBaseClient } from '@/services/httpBaseClient';
import type { AuthStoreInterface } from '@/stores/auth';
import { recordApiResponse } from '@/utils/apiRecorder';
import { v4 as uuidv4 } from 'uuid';
import { use401Handler } from '@/composables/use401Handler';

/**
 * HTTP client for Boomtown API. All requests use credentials: 'include' (fetch) or
 * withCredentials: true (XHR) so the session cookie is sent. CSRF token is sent in
 * X-Boomtown-Csrf-Token and refreshed from API responses. See README "Session and Boomtown API".
 */
interface RequestOptions {
	params?: Record<string, string>;
	body?: any;
	headers?: Record<string, string>;
	onProgress?: (progress: number) => void;
}

class HttpClient extends HttpBaseClient {
	private clientInstanceId: string;
	private onAuthChange?: (isAuthenticated: boolean) => void;
	private initialized: boolean = false;
	private initializing: Promise<void> | null = null;
	private isPartnerDataInitialized: boolean = false;
	private authStore: AuthStoreInterface | null = null;

	constructor(baseUrl: string, clientInstanceId: string, onAuthChange?: (isAuthenticated: boolean) => void) {
		super(baseUrl);
		this.clientInstanceId = clientInstanceId;
		this.onAuthChange = onAuthChange;
	}

	public setBaseUrl(baseUrl: string): void {
		this.baseUrl = baseUrl;
	}
	
	// Method to set auth store reference
	public setAuthStore(authStore: AuthStoreInterface): void {
		this.authStore = authStore;
	}

	private getCommonHeaders(): Record<string, string> {
		const headers: Record<string, string> = {
			'x-boomtown-client-instance-id': this.clientInstanceId,
			'x-request-id': uuidv4(),
			'x-boomtown-app': 'cxme',
			'x-requested-with': 'XMLHttpRequest',
			'content-type': 'application/json',
		};

		// X-Boomtown-Csrf-Token (header name is case-insensitive); refresh when API returns new token
		if (this.authStore && this.authStore.csrfToken) {
			headers['x-boomtown-csrf-token'] = this.authStore.csrfToken;
		}

		return headers;
	}

	private hasRelayCookie(): boolean {
		return document.cookie.split(';').some((cookie) => cookie.trim().startsWith('relay='));
	}

	async initialize(): Promise<void> {
		// If already initialized or initializing, return existing promise
		if (this.initialized) return Promise.resolve();
		if (this.initializing) return this.initializing;

		this.initializing = new Promise<void>(async (resolve) => {
			try {
				this.initialized = true;
				resolve();
			} catch (error) {
				console.debug('Error during initialization:', error);
				this.initialized = true;
				resolve(); // Resolve anyway to allow the app to continue
			} finally {
				this.initializing = null;
			}
		});

		return this.initializing;
	}

	protected async request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
		// Dev: warn if Boomtown API request may be missing auth (cookie is browser-sent; we only control CSRF here)
		if (import.meta.env.DEV && (this.baseUrl.includes('goboomtown.com') || this.baseUrl.includes('localhost'))) {
			if (!this.authStore) {
				console.warn('[HttpClient] Boomtown API request with no auth store set — CSRF and 401 handling may be wrong.', { method, path });
			} else if (!this.authStore.csrfToken) {
				console.warn('[HttpClient] Boomtown API request with empty CSRF token — server may return 401.', { method, path });
			}
		}

		// Build the query string if we have params
		let queryString = '';
		if (options.params) {
			// Use manual encoding to ensure spaces are encoded as %20 instead of +
			// URLSearchParams encodes spaces as + which some APIs don't handle correctly
			const params = Object.entries(options.params)
				.map(([key, value]) => {
					// Encode both key and value using encodeURIComponent which uses %20 for spaces
					return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
				})
				.join('&');
			queryString = params ? `?${params}` : '';
		}

		let reqBody = options.body || undefined;
		const isFormData = false;

		const headers = { ...this.getCommonHeaders(), ...options.headers };

		if (reqBody) {
			// Check if body is FormData (for file uploads)
			if (reqBody instanceof FormData) {
				// Remove content-type header to let browser set it with boundary
				delete headers['content-type'];
			} else if (reqBody instanceof URLSearchParams) {
				// For URLSearchParams, set the correct content-type and don't stringify
				headers['content-type'] = 'application/x-www-form-urlencoded';
				// URLSearchParams can be sent as-is
			} else {
				// For non-FormData, check stringify flag
				const ignoreStringify = reqBody.stringify === false;

				delete reqBody.stringify;

				reqBody = ignoreStringify ? reqBody : JSON.stringify(reqBody);
			}
		}

		// Use app origin when cross-origin so requests go through proxy and avoid CORS (see HttpBaseClient.getEffectiveBaseUrl)
		const base = this.getEffectiveBaseUrl();
		const fullUrl = `${base}/${path.replace(/^\//, '')}${queryString}`;

		// Use XMLHttpRequest for progress tracking or FormData uploads
		if (options.onProgress || isFormData) {
			return this.requestWithProgress<T>(method, fullUrl, reqBody, options);
		}

		// Don't set content-type for FormData, let browser set it with boundary
		if (isFormData) {
			delete headers['content-type'];
		}

		// Create AbortController for this request
		const abortController = new AbortController();
		this.activeRequests.add(abortController);

		try {
			// credentials: 'include' so session cookie is sent (partition must match appv5 top-level)
			const response = await fetch(fullUrl, {
				method,
				headers,
				body: reqBody,
				credentials: 'include',
				mode: 'cors',
				signal: abortController.signal,
			});
			
			// Remove from active requests on completion
			this.activeRequests.delete(abortController);

			if (!response.ok) {
			if (response.status === 401) {
				// Only trigger logout if we're currently authenticated
				// This prevents cascading logout calls during auto-logout process
				
				// Skip 401 handling during login/logout transitions
				// Note: If you log out while API calls are in-flight, the server session may be in an
				// inconsistent state. A page refresh after login will resolve this. This is a known edge case.
				if (this.authStore?.isLoggingIn) {
					// Don't trigger handler, just reject the request
					throw new Error(`HTTP error! status: ${response.status}`);
				}
				
				if (this.authStore?.isLoggingOut) {
					// Don't trigger handler, just reject the request
					throw new Error(`HTTP error! status: ${response.status}`);
				}
				
				if (this.authStore?.isAuthenticated) {
					// Use centralized 401 handler for user notification
					const { handle401WithLogout } = use401Handler();
					handle401WithLogout('accessing the API').catch(console.error);
					
					this.onAuthChange?.(false);
				}
			}
			
			// Try to read error response body for better error messages
			let errorData: any = null;
			try {
				errorData = await response.json();
			} catch (e) {
				// If we can't parse JSON, just use status
			}
			
			// Create error with response data attached
			const error: any = new Error(`HTTP error! status: ${response.status}`);
			error.status = response.status;
			error.response = errorData;
			throw error;
		}

		const responseData = await response.json();

		// Record the API response
		const functionName = new Error().stack?.split('\n')[2]?.match(/at\s+(\w+)/)?.[1] || 'unknown';
		recordApiResponse(functionName, fullUrl, method, responseData);

		// Update CSRF token if present in response
		if (responseData.csrf_token && this.authStore) {
			this.authStore.csrfToken = responseData.csrf_token;
			// Also update global headers
			const { updateBoomtownHeaders } = await import('@/services/BoomtownHeaders');
			updateBoomtownHeaders(responseData.csrf_token, this.clientInstanceId);
		}

			return responseData;
		} catch (error) {
			// Remove from active requests on error
			this.activeRequests.delete(abortController);
			
			// Check if request was aborted
			if (error instanceof Error && error.name === 'AbortError') {
				console.debug('Request aborted:', fullUrl);
				throw new Error('Request cancelled');
			}
			
			throw error;
		}
	}

	private async requestWithProgress<T>(
		method: string,
		fullUrl: string,
		body: any,
		options: RequestOptions
	): Promise<T> {
		return new Promise((resolve, reject) => {
			const xhr = new XMLHttpRequest();
			
			// Track this XHR request so it can be cancelled
			const xhrAbortController = {
				abort: () => xhr.abort(),
				signal: null as any // XHR doesn't use AbortSignal
			};
			this.activeRequests.add(xhrAbortController as any);

			// Set up progress tracking for uploads
			if (options.onProgress && xhr.upload) {
				xhr.upload.addEventListener('progress', (event) => {
					if (event.lengthComputable) {
						const percentComplete = Math.round((event.loaded / event.total) * 100);
						options.onProgress!(percentComplete);
					}
				});
			}

			xhr.addEventListener('load', () => {
				// Remove from active requests on completion
				this.activeRequests.delete(xhrAbortController as any);
				
				if (xhr.status >= 200 && xhr.status < 300) {
					try {
						const responseData = JSON.parse(xhr.responseText);

						// Record the API response
						const functionName = new Error().stack?.split('\n')[3]?.match(/at\s+(\w+)/)?.[1] || 'unknown';
						recordApiResponse(functionName, fullUrl, method, responseData);

						// Update CSRF token if present in response
						if (responseData.csrf_token && this.authStore) {
							this.authStore.csrfToken = responseData.csrf_token;
							// Also update global headers - use dynamic import to avoid circular dependencies
							import('@/services/BoomtownHeaders').then(({ updateBoomtownHeaders }) => {
								updateBoomtownHeaders(responseData.csrf_token, this.clientInstanceId);
							});
						}

						resolve(responseData);
					} catch (error) {
						reject(new Error('Failed to parse response JSON'));
					}
				} else {
					if (xhr.status === 401) {
						// Only trigger logout if we're currently authenticated
						// This prevents cascading logout calls during auto-logout process
						
						// Skip 401 handling during login/logout transitions
						if (this.authStore?.isLoggingIn) {
							reject(new Error(`HTTP error! status: ${xhr.status}`));
							return;
						}
						
						if (this.authStore?.isLoggingOut) {
							reject(new Error(`HTTP error! status: ${xhr.status}`));
							return;
						}
						
						if (this.authStore?.isAuthenticated) {
							// Use centralized 401 handler for user notification
							const { handle401WithLogout } = use401Handler();
							handle401WithLogout('uploading data').catch(console.error);
							
							this.onAuthChange?.(false);
						}
					}
					reject(new Error(`HTTP error! status: ${xhr.status}`));
				}
			});

			xhr.addEventListener('error', () => {
				// Remove from active requests on error
				this.activeRequests.delete(xhrAbortController as any);
				reject(new Error('Network error occurred during request'));
			});

			xhr.addEventListener('abort', () => {
				// Remove from active requests on abort
				this.activeRequests.delete(xhrAbortController as any);
				console.debug('XHR request aborted:', fullUrl);
				reject(new Error('Request cancelled'));
			});

			// Open the request
			xhr.open(method, fullUrl);

			// Set headers
			const headers = {
				...this.getCommonHeaders(),
				...options.headers,
			};

			// Don't set content-type for FormData, let browser set it with boundary
			if (body instanceof FormData) {
				delete headers['content-type'];
			}

			Object.entries(headers).forEach(([key, value]) => {
				xhr.setRequestHeader(key, value);
			});

			// withCredentials: true so session cookie is sent on XHR (same partition as appv5)
			xhr.withCredentials = true;

			// Send the request
			xhr.send(body);
		});
	}

	getCookieDSToken(): string {
		// Look for the dstoken cookie in the document cookies
		const cookies = document.cookie.split(';');
		for (const cookie of cookies) {
			const [name, value] = cookie.trim().split('=');
			if (name === 'dstoken') {
				return value;
			}
		}
		return '';
	}

	get url(): string {
		return this.baseUrl;
	}

	// Getter for client instance ID
	get instanceId(): string {
		return this.clientInstanceId;
	}

	// Getter for CSRF token from auth store
	get csrf(): string {
		return this.authStore?.csrfToken || '';
	}

	// Add isInitialized getter
	get isInitialized(): boolean {
		return this.initialized;
	}

	// Add getter for authentication status from auth store
	get isAuthenticated(): boolean {
		return Boolean(this.authStore?.isAuthenticated);
	}

	// Override HTTP method helpers to support progress tracking
	async get<T>(path: string, params?: Record<string, string>, headers?: Record<string, string>): Promise<T> {
		return this.request<T>('GET', path, { params, headers });
	}

	async post<T>(
		path: string,
		body: any,
		params?: Record<string, string>,
		headers?: Record<string, string>,
		onProgress?: (progress: number) => void
	): Promise<T> {
		return this.request<T>('POST', path, { body, params, headers, onProgress });
	}

	async put<T>(
		path: string,
		body: any,
		params?: Record<string, string>,
		headers?: Record<string, string>,
		onProgress?: (progress: number) => void
	): Promise<T> {
		return this.request<T>('PUT', path, { body, params, headers, onProgress });
	}

	async patch<T>(
		path: string,
		body: any,
		params?: Record<string, string>,
		headers?: Record<string, string>,
		onProgress?: (progress: number) => void
	): Promise<T> {
		return this.request<T>('PATCH', path, { body, params, headers, onProgress });
	}

	async delete<T>(path: string, params?: Record<string, string>, headers?: Record<string, string>): Promise<T> {
		return this.request<T>('DELETE', path, { params, headers });
	}

	/**
	 * Download a file from the given URL with proper authentication headers
	 * @param url The full URL to download from
	 * @returns A blob containing the file data and the suggested filename
	 */
	async downloadFile(url: string): Promise<{ blob: Blob; filename: string }> {
		const headers = this.getCommonHeaders();
		
		// Create AbortController for this request
		const abortController = new AbortController();
		this.activeRequests.add(abortController);

		try {
			// credentials: 'include' for Boomtown API so session cookie is sent
			const response = await fetch(url, {
				method: 'GET',
				headers,
				credentials: 'include',
				mode: 'cors',
				signal: abortController.signal,
			});
			
			// Remove from active requests on completion
			this.activeRequests.delete(abortController);

			if (!response.ok) {
				if (response.status === 401) {
					// Only trigger logout if we're currently authenticated
					if (this.authStore?.isLoggingIn || this.authStore?.isLoggingOut) {
						throw new Error(`HTTP error! status: ${response.status}`);
					}
					
					if (this.authStore?.isAuthenticated) {
						const { handle401WithLogout } = use401Handler();
						handle401WithLogout('downloading file').catch(console.error);
						this.onAuthChange?.(false);
					}
				}
				
				const error: any = new Error(`Download failed: ${response.status} ${response.statusText}`);
				error.status = response.status;
				throw error;
			}

			// Get the filename from Content-Disposition header if available
			const contentDisposition = response.headers.get('Content-Disposition');
			let filename = 'download.csv';
			if (contentDisposition) {
				const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
				if (filenameMatch && filenameMatch[1]) {
					filename = filenameMatch[1].replace(/['"]/g, '');
				}
			}

			// Convert response to blob
			const blob = await response.blob();

			return { blob, filename };
		} catch (error) {
			// Remove from active requests on error
			this.activeRequests.delete(abortController);
			
			// Check if request was aborted
			if (error instanceof Error && error.name === 'AbortError') {
				console.debug('Download request aborted:', url);
				throw new Error('Download cancelled');
			}
			
			throw error;
		}
	}
}

// Export the class for creating instances
export { HttpClient };
